import { useState } from 'react';
import { Alert, Button, Card, Center, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import type { Actor } from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';

/**
 * Приём приглашения.
 *
 * Самостоятельной регистрации в системе нет: попасть внутрь можно только по
 * одноразовому токену с коротким сроком. Экран доступен без входа — человек,
 * которого приглашают, ещё не имеет учётной записи.
 */
export function AcceptInvite(): React.JSX.Element {
  const [params] = useSearchParams();
  const setSession = useSession((s) => s.setSession);
  const token = params.get('token') ?? '';

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = repeat.length > 0 && password !== repeat;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ accessToken: string; actor: Actor }>('/auth/accept-invite', {
        method: 'POST',
        body: { token, password, displayName },
      });
      setSession(result.accessToken, result.actor);
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept the invitation');
    } finally {
      setBusy(false);
    }
  };

  if (token === '') {
    return (
      <Center mih="100dvh" p="md">
        <Card w={396} padding="lg">
          <Text fw={600}>Invitation link is incomplete</Text>
          <Text c="dimmed" fz="sm" mt={4}>
            Open the link exactly as it was sent to you. Ask your fleet administrator to send a new
            one if it no longer works.
          </Text>
        </Card>
      </Center>
    );
  }

  return (
    <Center mih="100dvh" p="md">
      <Card w={396} padding="lg" shadow="sm">
        <Stack gap="md">
          <div>
            <Text fw={680} fz="lg" style={{ letterSpacing: '-0.02em' }}>
              ProofFleet
            </Text>
            <Text c="dimmed" fz="sm">
              Set up your account
            </Text>
          </div>

          <TextInput
            label="Your name"
            description="Shown next to everything you do in the system"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />
          <PasswordInput
            label="Password"
            description="At least 12 characters"
            autoComplete="new-password"
            error={tooShort ? 'At least 12 characters' : null}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
          <PasswordInput
            label="Repeat password"
            autoComplete="new-password"
            error={mismatch ? 'Passwords do not match' : null}
            value={repeat}
            onChange={(e) => setRepeat(e.currentTarget.value)}
          />

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            fullWidth
            loading={busy}
            disabled={displayName.trim() === '' || password.length < 12 || password !== repeat}
            onClick={() => void submit()}
          >
            Create account
          </Button>
        </Stack>
      </Card>
    </Center>
  );
}
